import { PlusIcon, UploadIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { MODAL_AI_AGENT_CREATE } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { pushModal } from '@/redux/modals/actions';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';

interface EmptyAIAgentsProps {
  disabled?: boolean;
  disabledTooltip?: string;
}

/**
 * EmptyAIAgents - Empty state component shown when no AI agents exist
 */
export const EmptyAIAgents = ({ disabled = false, disabledTooltip }: EmptyAIAgentsProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleCreateClick = () => {
    trackEvent(EventName.ClickCreateBrain);
    dispatch(pushModal(MODAL_AI_AGENT_CREATE));
  };

  const handleImportClick = () => {
    dispatch(pushModal(MODAL_AI_AGENT_CREATE, { startWithImport: true }));
  };

  const actions = (
    <div className="flex items-center gap-3">
      <Button variant="outline-neutral" size="sm" onClick={handleImportClick} disabled={disabled}>
        <UploadIcon size={16} />
        {t('common.import')}
      </Button>
      <Button size="sm" onClick={handleCreateClick} disabled={disabled}>
        <PlusIcon size={16} />
        {t('common.create')}
      </Button>
    </div>
  );

  return (
    <Empty>
      <EmptyHeader>
        <EmptyMedia>
          <img src="/assets/empty-states/ai-agents.webp" alt="" />
        </EmptyMedia>
        <EmptyTitle>{t('ai_agents.create_ai_agent')}</EmptyTitle>
        <EmptyDescription>{t('ai_agents.empty_state.description')}</EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        {disabled && disabledTooltip ? (
          <InfoTooltip content={disabledTooltip}>
            <span>{actions}</span>
          </InfoTooltip>
        ) : (
          actions
        )}
      </EmptyContent>
    </Empty>
  );
};
